import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import screenerClient from "../apollo/screenerClient";
import { logEvent } from "../actions";
import StartScreen from "../components/screener/StartScreen";
import SectionScreen from "../components/screener/SectionScreen";
import ResultsScreen from "../components/screener/ResultsScreen";
import ReviewWithPediatricianScreen from "../components/screener/ReviewWithPediatricianScreen";
import CompleteScreen from "../components/screener/CompleteScreen";
import {
  CaregiverAnswers,
  LikertAnswer,
  PscAnswer,
  PscAnswers,
  ScreenerResult,
  scoreScreener
} from "../components/screener/scoring";
import "./Screener.css";

type Step = "start" | "section1" | "section2" | "section3" | "section4" | "results" | "review" | "complete";

const SECTION_STEPS: readonly Step[] = ["section1", "section2", "section3", "section4"];

const STEP_ORDER: readonly Step[] = [
  "start",
  "section1",
  "section2",
  "section3",
  "section4",
  "results",
  "review",
  "complete"
];

export type AgeBand = "4–5" | "6–11" | "12–17";

export type SelectedProvider = {
  readonly externalOrganizationId: number;
  readonly externalProviderId: number;
  readonly providerName: string;
};

type ScreenerState = {
  readonly step: Step;
  readonly ageBand: AgeBand | null;
  readonly provider: SelectedProvider | null;
  readonly pscAnswers: PscAnswers;
  readonly caregiverAnswers: CaregiverAnswers;
};

const initialState: ScreenerState = {
  step: "start",
  ageBand: null,
  provider: null,
  pscAnswers: {},
  caregiverAnswers: {}
};

function ScreenerFlow() {
  const [state, setState] = useState<ScreenerState>(initialState);

  useEffect(() => {
    logEvent("screener_started");
  }, []);

  const goToStep = useCallback((step: Step) => {
    setState(prev => ({ ...prev, step }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const goBack = useCallback(() => {
    setState(prev => {
      const index = STEP_ORDER.indexOf(prev.step);
      const previous = index > 0 ? STEP_ORDER[index - 1] : prev.step;
      return { ...prev, step: previous };
    });
  }, []);

  const goForward = useCallback((fromStep: Step) => {
    const completedSection = SECTION_STEPS.indexOf(fromStep);
    if (completedSection >= 0) {
      logEvent("section_completed", { section: completedSection + 1 });
    }
    setState(prev => {
      const index = STEP_ORDER.indexOf(prev.step);
      const next = index < STEP_ORDER.length - 1 ? STEP_ORDER[index + 1] : prev.step;
      return { ...prev, step: next };
    });
  }, []);

  const setAgeBand = useCallback((ageBand: AgeBand) => {
    setState(prev => ({ ...prev, ageBand }));
  }, []);

  const setProvider = useCallback((provider: SelectedProvider | null) => {
    setState(prev => ({ ...prev, provider }));
  }, []);

  const setPscAnswer = useCallback((id: string, value: PscAnswer) => {
    setState(prev => ({ ...prev, pscAnswers: { ...prev.pscAnswers, [id]: value } }));
  }, []);

  const setCaregiverAnswer = useCallback((id: string, value: LikertAnswer) => {
    setState(prev => ({ ...prev, caregiverAnswers: { ...prev.caregiverAnswers, [id]: value } }));
  }, []);

  const result: ScreenerResult = useMemo(
    () => scoreScreener(state.pscAnswers, state.caregiverAnswers),
    [state.pscAnswers, state.caregiverAnswers]
  );

  useEffect(() => {
    if (state.step === "results") {
      logEvent("results_viewed", { band: result.band });
    }
  }, [state.step, result.band]);

  const sectionIndex = SECTION_STEPS.indexOf(state.step);

  return (
    <div className="screener-container">
      {state.step === "start" && (
        <StartScreen
          ageBand={state.ageBand}
          provider={state.provider}
          onAgeBandChange={setAgeBand}
          onProviderChange={setProvider}
          onBegin={() => goForward("start")}
        />
      )}
      {sectionIndex >= 0 && (
        <SectionScreen
          index={sectionIndex}
          pscAnswers={state.pscAnswers}
          caregiverAnswers={state.caregiverAnswers}
          onPscAnswer={setPscAnswer}
          onCaregiverAnswer={setCaregiverAnswer}
          onBack={goBack}
          onNext={() => goForward(state.step)}
        />
      )}
      {state.step === "results" && state.provider && state.ageBand && (
        <ResultsScreen
          result={result}
          provider={state.provider}
          ageBand={state.ageBand}
          onConnected={() => goToStep("complete")}
          onReviewWithPediatrician={() => goToStep("review")}
        />
      )}
      {state.step === "review" && state.provider && state.ageBand && (
        <ReviewWithPediatricianScreen
          result={result}
          provider={state.provider}
          ageBand={state.ageBand}
          onBack={() => goToStep("results")}
          onSubmitted={() => goToStep("complete")}
        />
      )}
      {state.step === "complete" && <CompleteScreen onReset={reset} />}
    </div>
  );
}

export default function Screener() {
  return (
    <ApolloProvider client={screenerClient}>
      <ScreenerFlow />
    </ApolloProvider>
  );
}
